import { type CSSProperties, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ArrowForward, ArrowRight } from "@mui/icons-material";
import { Box, Divider, MenuList, styled } from "@schaeffler-commerce/design-system";
import { RouterLink } from "~/common/components/Link";
import useMediaQuery, { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import { isSiteRelativeUrl, resolveUrl } from "~/utils/url/urlResolver";
import type { TreeNode } from "~/domains/navigation/types/NavigationTypes";
import { NavigationItem as StyledNavigationItem } from "~/features/categoryNavigation/components/CategoryNavigation.styles";
import { getFontStyle, FontSize } from "@schaeffler-commerce/design-system";

interface NavigationTreeProps {
  tree: TreeNode;
  visibleNodes: TreeNode[];
  onItemClick?: (node: TreeNode) => void;
  onItemHover?: (node: TreeNode, noChildren?: boolean) => void;
  onItemLeave?: (node: TreeNode) => void;
}

function collectAllNodes(node: TreeNode): TreeNode[] {
  const result: TreeNode[] = [node];
  for (const child of node.childNodes) {
    result.push(...collectAllNodes(child));
  }
  return result;
}

export const NavigationTree = ({ tree, visibleNodes, onItemClick, onItemHover, onItemLeave }: NavigationTreeProps) => {
  const { t } = useTranslation();

  const allNodes = useMemo(() => collectAllNodes(tree), [tree]);

  return (
    <StyledNav aria-label={t("navigation.categoryNavigation")}>
      {allNodes.map((node) => (
        <NavigationLevel
          key={node.id}
          node={node}
          visibleNodes={visibleNodes}
          onItemClick={onItemClick}
          onItemHover={onItemHover}
          onItemLeave={onItemLeave}
        />
      ))}
    </StyledNav>
  );
};

interface NavigationLevelProps {
  node: TreeNode;
  visibleNodes: TreeNode[];
  onItemClick?: (node: TreeNode) => void;
  onItemHover?: (node: TreeNode, noChildren?: boolean) => void;
  onItemLeave?: (node: TreeNode) => void;
}

const NavigationLevel = ({ node, visibleNodes, onItemClick, onItemHover, onItemLeave }: NavigationLevelProps) => {
  const { t } = useTranslation();
  const isVisible = visibleNodes.includes(node);
  const isHoverEnabled = useMediaQuery("(hover: hover)");

  if (node.childNodes.length === 0) {
    return null;
  }

  const titleContent = (
    <>
      <span>{t(node.title)}</span>
      <ArrowForward />
    </>
  );
  // Tracking attributes keep the public url shape; the link `to` below stays
  // router-space so the router basepath joins the prefix exactly once.
  const titleAttributes = {
    "tracking-aux-navigation-name": `Overview ${t(node.title, { lng: "en" })}`,
    "tracking-aux-navigation-url": node.url ? resolveUrl(node.url) : undefined,
    "tracking-aux-navigation-level": `Level ${node.level + 1}`,
  };
  const titleTarget = node.openInNewTab ? "_blank" : undefined;
  // Site-relative urls navigate through the router; absolute / protocol-relative / hash urls
  // stay raw anchors (RouterLink would mangle them under the basepath).
  const rawTitleHref = node.url && !isSiteRelativeUrl(node.url) ? node.url : undefined;
  const viewAllEntry = (
    <StyledNavigationItem>
      <span>{t("notifications.viewAll")}</span>
    </StyledNavigationItem>
  );

  return (
    <StyledNavigationLevel
      isVisible={isVisible}
      style={{ "--desktop-column": node.level + 1 } as CSSProperties}
      tracking-aux-navigation={"true"}
    >
      {node.level >= 0 && (
        <Box
          sx={(theme) => ({
            position: "sticky",
            top: 0,
            zIndex: 1,
            backgroundColor: theme.palette.primary.contrastText,
          })}
        >
          {node.url && isSiteRelativeUrl(node.url) ? (
            <NavigationLevelTitle to={node.url} target={titleTarget} {...titleAttributes}>
              {titleContent}
            </NavigationLevelTitle>
          ) : (
            <NavigationLevelTitle as="a" href={rawTitleHref} target={titleTarget} {...titleAttributes}>
              {titleContent}
            </NavigationLevelTitle>
          )}
          <Divider sx={{ marginBlock: 1 }} />
        </Box>
      )}
      <MenuList>
        {!isHoverEnabled &&
          node.url &&
          (isSiteRelativeUrl(node.url) ? (
            <RouterLink to={node.url}>{viewAllEntry}</RouterLink>
          ) : (
            <a href={node.url}>{viewAllEntry}</a>
          ))}
        {node.childNodes.map((childNode) => (
          <NavigationTreeItem
            key={childNode.id}
            node={childNode}
            selected={visibleNodes.includes(childNode)}
            onClick={onItemClick}
            onMouseEnter={onItemHover}
            onMouseLeave={onItemLeave}
          />
        ))}
      </MenuList>
    </StyledNavigationLevel>
  );
};

interface NavigationTreeItemProps {
  node: TreeNode;
  selected: boolean;
  onClick?: (node: TreeNode) => void;
  onMouseEnter?: (node: TreeNode, noChildren?: boolean) => void;
  onMouseLeave?: (node: TreeNode) => void;
}

const NavigationTreeItem = ({ node, selected, onClick, onMouseEnter, onMouseLeave }: NavigationTreeItemProps) => {
  const { t } = useTranslation();
  const isHoverEnabled = useMediaQuery("(hover: hover)");
  const isMinDesktop = useIsMinDesktopSize();
  const hasChildren = node.childNodes.length > 0;

  const itemAttributes = {
    selected,
    "tracking-aux-navigation-name": t(node.title, { lng: "en" }),
    "tracking-aux-navigation-url": node.url ? resolveUrl(node.url) : undefined,
    "tracking-aux-navigation-level": `category level ${node.level}`,
  };
  const itemContent = (
    <>
      {node.smallImage && <img className={"icon"} src={node.smallImage.url} alt={node.smallImage.altText ?? ""} />}
      <span>{t(node.title)}</span>
      {hasChildren && <ArrowRight sx={{ marginLeft: "auto" }} />}
    </>
  );

  if ((isHoverEnabled && isMinDesktop) || !hasChildren) {
    // Site-relative urls navigate through the router; absolute / hash urls stay raw anchors.
    const target = node.openInNewTab ? "_blank" : undefined;
    const hoverProps = {
      onMouseEnter: () => onMouseEnter?.(node, !hasChildren),
      onMouseLeave: () => onMouseLeave?.(node),
    };
    return node.url && isSiteRelativeUrl(node.url) ? (
      <StyledNavigationItem component={RouterLink} to={node.url} target={target} {...hoverProps} {...itemAttributes}>
        {itemContent}
      </StyledNavigationItem>
    ) : (
      <StyledNavigationItem component="a" href={node.url} target={target} {...hoverProps} {...itemAttributes}>
        {itemContent}
      </StyledNavigationItem>
    );
  }

  return (
    <StyledNavigationItem onClick={() => onClick?.(node)} {...itemAttributes}>
      {itemContent}
    </StyledNavigationItem>
  );
};

const StyledNav = styled("nav")(({ theme }) => ({
  position: "relative",
  display: "grid",
  overflow: "hidden",
  width: "100%",

  [theme.breakpoints.down("desktop-s")]: {
    "& > *": {
      gridRow: "1",
      gridColumn: "1",
    },
  },

  [theme.breakpoints.up("desktop-s")]: {
    gridAutoColumns: "288px",
    gridAutoFlow: "column",
    gap: theme.spacing(2),
  },
  [theme.breakpoints.up("desktop-m")]: {
    gridAutoColumns: "358px",
  },
}));

const StyledNavigationLevel = styled("div")<{ isVisible: boolean }>(({ theme, isVisible }) => ({
  [theme.breakpoints.down("desktop-s")]: {
    visibility: isVisible ? "visible" : "hidden",
    height: isVisible ? "auto" : 0,
    overflow: isVisible ? "auto" : "hidden",
    pointerEvents: isVisible ? "auto" : "none",
    opacity: isVisible ? 1 : 0,
    position: isVisible ? "relative" : "absolute",
    transition: theme.transitions.create(["visibility", "height", "opacity"], {
      duration: theme.transitions.duration.short,
    }),
  },

  [theme.breakpoints.up("desktop-s")]: {
    display: isVisible ? "block" : "none",
    gridColumn: "var(--desktop-column, 1)",
    gridRow: 1,

    maxHeight: `calc(100dvh - ${theme.spacing(4)} - var(--drawer-offset, 0px))`,
    overflowY: "auto",
    overflowX: "hidden",
    scrollbarWidth: "thin",
    scrollbarColor: "transparent transparent",
    "&::-webkit-scrollbar": { width: theme.spacing(1) },
    "&::-webkit-scrollbar-track": { background: theme.palette.surface.main, borderRadius: theme.spacing(0.5) },
    "&::-webkit-scrollbar-thumb": { background: theme.palette.text.secondary, borderRadius: theme.spacing(0.5) },
    "&:hover": { scrollbarColor: `${theme.palette.text.secondary} transparent` },
  },
}));

const NavigationLevelTitle = styled(RouterLink)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(1, 2),
  textDecoration: "none",
  color: theme.palette.text.primary,
  ...getFontStyle(FontSize.TitleMedium),
  gap: "8px",
  "&:hover": {
    color: theme.palette.primary.main,
    cursor: "pointer",
    textDecoration: "underline",
  },
}));
